import { useState, useEffect, useMemo } from "react";

// formik components
import { Formik, Form, useFormikContext } from "formik";

// toast notifications
import { toast } from "react-toastify";

// Material UI components
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Page components

// API queries
import { useCountryLocCodes } from "api/features/country/queries";
import { useExportEpidemiologyData } from "api/features/export/queries";

// Contexts
import { useUserContext } from "contexts/contextUser";

import validations from "../../schemas/countrySchema/validations";
import getForm from "../../schemas/countrySchema/form";

import { useFormAnalyticsContext } from "contexts/contextFormAnalytics";
import ExportCountryForm from "./sections/ExportCountryForm";

function Export1CountryForm(): JSX.Element {
  const { liveFormValues, setAppliedOptions } = useFormAnalyticsContext();

  const { data: countryLocCodes } = useCountryLocCodes();
  const { formId, formField } = getForm(countryLocCodes);

  const exportClient = useExportEpidemiologyData();

  const handleSubmit = (values: any, actions: any) => {
    if (exportClient.isPending) {
      actions.setSubmitting(false);
      return;
    }

    // Refresh the graphs displayed in the dashboard with the new options before exporting
    setAppliedOptions(values);

    const toastId = toast.loading("Exporting data...");

    exportClient.mutate(values, {
      onSuccess: () => {
        toast.update(toastId, {
          render: "Exported successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2500,
        });
      },
      onError: () => {
        toast.update(toastId, {
          render: "Failed to export data.",
          type: "error",
          isLoading: false,
          autoClose: 2500,
        });
      },
      onSettled: () => {
        actions.setSubmitting(false);
      },
    });
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={liveFormValues}
        validationSchema={validations}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting }) => {
          const formData = { values, errors, touched, formField };
          const isExporting = isSubmitting || exportClient.isPending;
          return (
            <Form id={formId} autoComplete="off">
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <MDTypography variant="h6">One Country to export</MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <MDTypography variant="h6" sx={{ whiteSpace: "pre-wrap" }}>
                    Export one country to CSV file:
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
                    Description: Select one country to export data for. The exported data will be in
                    CSV format and will include the selected country&apos;s information. It is
                    possible to change the default parameters for the country before exporting.
                  </MDTypography>

                  <ExportCountryForm formData={formData} />

                  <MDButton
                    disabled={isExporting || Object.keys(errors).length > 0}
                    type="submit"
                    variant="gradient"
                    color="dark"
                  >
                    Export
                  </MDButton>
                </AccordionDetails>
              </Accordion>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export default Export1CountryForm;
